const STATUS_LABELS = {
  // niebieskie
  NEW_LEAD: 'Nowy lead',
  CONTACTED: 'Skontaktowany',
  TEST: 'Testuje',
  // cyjan
  BIG_CUSTOMER_POTENTIAL: 'Duży klient - może kiedyś',
  // pomarańczowe
  SEND_SAMPLES: 'Wyślij próbki',
  SEND_EMAIL_OFFER: 'Wyślij maila',
  WAITING_FOR_RESPONSE: 'Oczekiwanie na odpowiedź',
  // zielone
  EXEL_SEND: 'Excel wysłany',
  EMAIL_OFFER_SENT: 'Oferta mailowa wysłana',
  SAMPLES_SENT: 'Próbki wysłane',
  EMAIL_OFFER_AND_SAMPLES_SENT: 'Oferta mailowa i próbki wysłane',
  CUSTOMER: 'Klient',
  // szare
  NOT_INTERESTED: 'Niezainteresowany',
  DOES_NOT_SELL_SUCH_PRODUCTS: 'Nie sprzedaje takich produktów',
  // czerwony na końcu
  REJECTED: 'Odrzucony',
};

const PRIORITY_LABELS = {
  LOW: 'Niski',
  MEDIUM: 'Średni',
  HIGH: 'Wysoki',
};

const ACTIVITY_TYPE_LABELS = {
  NOTE: 'Notatka',
  TASK: 'Zadanie',
  REMINDER: 'Przypomnienie',
};

export const STATUS_COLORS = {
  NEW_LEAD: 'info',
  CONTACTED: 'primary',
  EMAIL_OFFER_SENT: 'success',
  SAMPLES_SENT: 'success',
  EMAIL_OFFER_AND_SAMPLES_SENT: 'success',
  EXEL_SEND: 'success',
  TEST: 'success',
  WAITING_FOR_RESPONSE: 'success',
  SEND_SAMPLES: 'warning',
  SEND_EMAIL_OFFER: 'warning',
  BIG_CUSTOMER_POTENTIAL: 'info',
  CUSTOMER: 'success',
  NOT_INTERESTED: 'default',
  DOES_NOT_SELL_SUCH_PRODUCTS: 'default',
  REJECTED: 'error',
};

export const PRIORITY_COLORS = {
  LOW: 'default',
  MEDIUM: 'warning',
  HIGH: 'error',
};

export const getStatusLabel = (status) => STATUS_LABELS[status] || status || '—';

const STATUS_CHIP_STYLES = {
  EXEL_SEND: {
    filled: { bgcolor: '#a5d6a7', color: '#1b5e20' },
    outlined: { borderColor: '#81c784', color: '#2e7d32' },
  },
  TEST: {
    filled: { bgcolor: '#42a5f5', color: '#ffffff' },
    outlined: { borderColor: '#2196f3', color: '#1565c0' },
  },
  WAITING_FOR_RESPONSE: {
    filled: { bgcolor: '#ffb300', color: '#1a1a1a' },
    outlined: { borderColor: '#ff8f00', color: '#e65100' },
  },
  EMAIL_OFFER_SENT: {
    filled: { bgcolor: '#aed581', color: '#33691e' },
    outlined: { borderColor: '#9ccc65', color: '#558b2f' },
  },
  SAMPLES_SENT: {
    filled: { bgcolor: '#aed581', color: '#33691e' },
    outlined: { borderColor: '#9ccc65', color: '#558b2f' },
  },
  EMAIL_OFFER_AND_SAMPLES_SENT: {
    filled: { bgcolor: '#aed581', color: '#33691e' },
    outlined: { borderColor: '#9ccc65', color: '#558b2f' },
  },
  BIG_CUSTOMER_POTENTIAL: {
    filled: { bgcolor: '#00FFFF', color: '#004d40' },
    outlined: { borderColor: '#00bcd4', color: '#006064' },
  },
};

export const STATUS_PIN_COLORS = {
  NEW_LEAD: '#0288d1',
  CONTACTED: '#1976d2',
  EMAIL_OFFER_SENT: '#9ccc65',
  SAMPLES_SENT: '#9ccc65',
  EMAIL_OFFER_AND_SAMPLES_SENT: '#9ccc65',
  EXEL_SEND: '#81c784',
  TEST: '#42a5f5',
  WAITING_FOR_RESPONSE: '#ffb300',
  SEND_SAMPLES: '#ed6c02',
  SEND_EMAIL_OFFER: '#ed6c02',
  BIG_CUSTOMER_POTENTIAL: '#00FFFF',
  CUSTOMER: '#2e7d32',
  NOT_INTERESTED: '#757575',
  DOES_NOT_SELL_SUCH_PRODUCTS: '#757575',
  REJECTED: '#d32f2f',
};

export const getStatusChipProps = (status, { variant = 'filled' } = {}) => {
  const customStyle = STATUS_CHIP_STYLES[status];
  if (customStyle) {
    return { sx: customStyle[variant] || customStyle.filled };
  }

  return { color: STATUS_COLORS[status] || 'default' };
};

export const buildStatusOptions = (variant = 'filled') =>
  Object.entries(STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
    variant,
    ...getStatusChipProps(value, { variant }),
  }));

/** Pola zgodne z CreateCustomerCommand / UpdateCustomerCommand */
export const CUSTOMER_COMMAND_FIELDS = [
  'companyName',
  'nip',
  'postalCode',
  'city',
  'address',
  'phone',
  'email',
  'contactPerson',
  'status',
  'priority',
  'notes',
];

const normalizeNipValue = (nip) => (nip || '').replace(/\D/g, '');

const normalizeLastContactDateValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00`;
  }
  return value;
};

const CLEARABLE_OPTIONAL_FIELDS = new Set(['nip', 'email', 'contactPerson', 'notes']);

const normalizeCommandFieldValue = (field, value) => {
  if (field === 'nip') {
    const nip = normalizeNipValue(value);
    if (nip) return nip;
    return CLEARABLE_OPTIONAL_FIELDS.has(field) ? null : undefined;
  }
  if (field === 'lastContactDate') {
    return normalizeLastContactDateValue(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed) return trimmed;
    return CLEARABLE_OPTIONAL_FIELDS.has(field) ? null : undefined;
  }
  return value ?? undefined;
};

export const buildCreateCustomerCommand = (form) => ({
  companyName: form.companyName.trim(),
  nip: normalizeNipValue(form.nip) || undefined,
  postalCode: form.postalCode.trim(),
  city: form.city.trim(),
  address: form.address.trim(),
  phone: form.phone.trim(),
  email: form.email?.trim() || undefined,
  contactPerson: form.contactPerson?.trim() || undefined,
  status: form.status,
  priority: form.priority,
  notes: form.notes?.trim() || undefined,
});

export const buildUpdateCustomerCommand = (fields) => {
  const payload = {};

  CUSTOMER_COMMAND_FIELDS.forEach((field) => {
    if (!Object.prototype.hasOwnProperty.call(fields, field)) return;
    payload[field] = normalizeCommandFieldValue(field, fields[field]);
  });

  if (Object.prototype.hasOwnProperty.call(fields, 'lastContactDate')) {
    payload.lastContactDate = normalizeLastContactDateValue(fields.lastContactDate);
  }

  return payload;
};

export const toDateInputValue = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getTodayDateInputValue = () => toDateInputValue(new Date());

export class ClientListItem {
  constructor(data) {
    this.id = data.id;
    this.companyName = data.companyName || '';
    this.city = data.city || '';
    this.address = data.address || '';
    this.postalCode = data.postalCode || data.postal_code || '';
    this.status = data.status || '';
    this.lastContactDate = data.lastContactDate || data.last_contact_date || null;
    this.createdBy = data.createdBy || data.created_by || '';
    this.hasActiveReminder = Boolean(data.hasActiveReminder ?? data.has_active_reminder);
    this.remindAt = data.remindAt || data.remind_at || null;
    this.reminderId = data.reminderId || data.reminder_id || data.activeReminderId || null;
    this.phoneNumber = data.phoneNumber || data.phone_number || data.phone || '';
    this.note = data.note || data.notes || '';
    this.lastComment = data.lastComment || data.last_comment || '';
  }

  getStatusLabel() {
    return getStatusLabel(this.status);
  }

  getCreatedByLabel() {
    return this.createdBy || '—';
  }

  static fromApi(data) {
    return new ClientListItem(data);
  }
}

export class ClientDetails {
  constructor(data) {
    this.id = data.id;
    this.companyName = data.companyName || '';
    this.city = data.city || '';
    this.nip = data.nip || '';
    this.address = data.address || '';
    this.postalCode = data.postalCode || data.postal_code || data.zipCode || data.zip_code || '';
    this.phone = data.phone || '';
    this.email = data.email || '';
    this.contactPerson = data.contactPerson || '';
    this.status = data.status || '';
    this.notes = data.notes || '';
    this.priority = data.priority || '';
    this.lastContactDate = data.lastContactDate || data.last_contact_date || null;
    this.createdBy = data.createdBy || data.created_by || '';
    this.hasActiveReminder = Boolean(data.hasActiveReminder ?? data.has_active_reminder);
    this.remindAt = data.remindAt || data.remind_at || null;
    this.reminderId = data.reminderId || data.reminder_id || data.activeReminderId || null;
    this.createdAt = data.createdAt || null;
    this.updatedAt = data.updatedAt || null;
  }

  getStatusLabel() {
    return getStatusLabel(this.status);
  }

  getPriorityLabel() {
    return PRIORITY_LABELS[this.priority] || this.priority || '—';
  }

  toFormData() {
    return {
      companyName: this.companyName,
      nip: this.nip,
      city: this.city,
      address: this.address,
      postalCode: this.postalCode,
      phone: this.phone,
      email: this.email,
      contactPerson: this.contactPerson,
      status: this.status || 'NEW_LEAD',
      priority: this.priority || 'MEDIUM',
      notes: this.notes,
      lastContactDate: toDateInputValue(this.lastContactDate),
    };
  }

  getEffectiveLastContactDate(latestActivityDate) {
    return this.lastContactDate || latestActivityDate || null;
  }

  getInvoiceCopyText() {
    const lines = [];

    if (this.companyName) lines.push(this.companyName);
    if (this.address) lines.push(this.address);

    const cityLine = [this.postalCode, this.city].filter(Boolean).join(' ');
    if (cityLine) lines.push(cityLine);

    if (this.nip) lines.push(`NIP: ${this.nip}`);

    return lines.join('\n');
  }

  static fromApi(data) {
    return new ClientDetails(data);
  }
}

export class CustomerReminder {
  constructor(data) {
    this.id = data.id;
    this.customerUuid = data.customerUuid || data.customer_uuid || null;
    this.customerActivityUuid = data.customerActivityUuid || data.customer_activity_uuid || null;
    this.remindAt = data.remindAt || data.remind_at || null;
  }

  static fromApi(data) {
    if (!data) return null;
    return new CustomerReminder(data);
  }
}

export class CustomerActivity {
  constructor(data) {
    this.id = data.id;
    this.type = data.type || 'NOTE';
    this.comment = data.comment || '';
    this.createdBy = data.createdBy || 'Nieznany';
    this.createdAt = data.createdAt || data.created_at || null;
    this.remindAt = data.remindAt || data.remind_at || null;
  }

  getTypeLabel() {
    return ACTIVITY_TYPE_LABELS[this.type] || this.type;
  }

  getDisplayDate() {
    if (this.type === 'REMINDER' && this.remindAt) {
      return this.remindAt;
    }
    return this.createdAt || null;
  }

  static fromApi(data) {
    return new CustomerActivity(data);
  }
}

export const CRM_LABELS = {
  STATUS_LABELS,
  PRIORITY_LABELS,
  ACTIVITY_TYPE_LABELS,
};

export const ALL_CUSTOMER_STATUSES = Object.keys(STATUS_LABELS);

export const CLIENT_LIST_SORTABLE_COLUMNS = [
  { field: 'companyName', label: 'Nazwa firmy' },
  { field: 'city', label: 'Miasto' },
  { field: 'status', label: 'Status' },
  { field: 'createdBy', label: 'Utworzył' },
  { field: 'lastContactDate', label: 'Dni od kontaktu' },
];

export const EMPTY_CUSTOMER_FORM = {
  companyName: '',
  nip: '',
  city: '',
  address: '',
  postalCode: '',
  phone: '',
  email: '',
  contactPerson: '',
  status: 'NEW_LEAD',
  priority: 'MEDIUM',
  notes: '',
};
